import React, { useEffect, useState } from "react";
import { useLanguage, pick } from "@/lib/lang";
import { sgb } from "@/lib/sgbContent";
import { TESTIDS } from "@/constants/testIds";

const TID = {
  ribbon: "sgb-ribbon",
  ribbonCta: "sgb-ribbon-cta",
  ribbonDismiss: "sgb-ribbon-dismiss",
};

const STORAGE_KEY = "sgb.ribbon.dismissed";
const REVEAL_AT = 0.35;

/**
 * Context-aware ribbon for SGB: it watches which section is in view and shows
 * the variant assigned to that section (reviews or clearsignal). It is intentionally
 * faceless — no personal name surfaces.
 */
const SGBRibbon = ({ scrollContainerRef, sectionsMap }) => {
  const { lang } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [activeScope, setActiveScope] = useState("reviews");

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.localStorage.getItem(STORAGE_KEY) === "1") {
      setDismissed(true);
      return;
    }

    // Visibility based on scroll percent
    const onScroll = () => {
      const h = document.documentElement;
      const denom = (h.scrollHeight - h.clientHeight) || 1;
      const pct = h.scrollTop / denom;
      setVisible(pct > REVEAL_AT);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    // IntersectionObserver on relevant sections to switch variant
    const obs = new IntersectionObserver(
      (entries) => {
        const inView = entries.filter((e) => e.isIntersecting);
        if (inView.length > 0) {
          const top = inView.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
          const scope = top.target.getAttribute("data-ribbon-scope");
          if (scope) setActiveScope(scope);
        }
      },
      { threshold: [0.25] }
    );

    Object.values(sectionsMap || {}).forEach((el) => {
      if (el?.current) obs.observe(el.current);
    });

    return () => {
      window.removeEventListener("scroll", onScroll);
      obs.disconnect();
    };
  }, [sectionsMap]);

  const dismiss = () => {
    setDismissed(true);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, "1");
    }
  };

  if (dismissed) return null;

  const variant = sgb.ribbons[activeScope] || sgb.ribbons.reviews;
  const isClearSignal = activeScope === "clearsignal";

  // Accent color per variant
  const accent = isClearSignal ? "#CBB7B0" : "#FF7100";

  return (
    <div
      aria-hidden={!visible}
      data-testid={TID.ribbon}
      data-scope={activeScope}
      className={[
        "fixed left-1/2 -translate-x-1/2 z-40 transition-all duration-700 bottom-5 md:bottom-7",
        visible ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-6 pointer-events-none",
      ].join(" ")}
    >
      <div className="relative flex items-stretch bg-depth text-canvas shadow-[0_18px_45px_rgba(40,0,27,0.35)] border border-warmth/20 rounded-full overflow-hidden">
        <div className="flex items-center pl-5 md:pl-7 pr-3 md:pr-5 py-2 md:py-2.5 gap-3 md:gap-4">
          <span
            className="inline-block w-2 h-2 rounded-full"
            style={{ background: accent }}
            aria-hidden
          />
          <p className="font-display italic text-base md:text-lg leading-none whitespace-nowrap">
            {pick(variant.whisper, lang)}
          </p>
          {variant.meta && pick(variant.meta, lang) && (
            <>
              <span className="hidden md:inline text-warmth/70 text-sm font-light">·</span>
              <p className="hidden md:inline font-mono text-[0.62rem] tracking-[0.28em] uppercase text-warmth/85">
                {pick(variant.meta, lang)}
              </p>
            </>
          )}
        </div>
        <a
          href={`#kontakt?scope=${encodeURIComponent(variant.scope)}`}
          data-testid={TID.ribbonCta}
          onClick={(e) => {
            e.preventDefault();
            const el = document.getElementById("kontakt");
            const topicSelect = document.querySelector('[data-testid="sgb-contact-topic"]');
            if (topicSelect) topicSelect.value = variant.scope;
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
          className="flex items-center gap-2 px-5 md:px-7 py-2.5 md:py-3 text-depth font-mono uppercase tracking-[0.28em] text-[0.62rem] md:text-[0.7rem] transition-colors duration-500"
          style={{ background: accent }}
        >
          {pick(variant.cta, lang)} <span aria-hidden>↗</span>
        </a>
        <button
          type="button"
          onClick={dismiss}
          data-testid={TID.ribbonDismiss}
          aria-label={lang === "de" ? "Leiste schließen" : "Dismiss"}
          className="px-3 md:px-4 text-warmth hover:text-canvas transition-colors text-lg leading-none border-l border-warmth/20"
        >
          ×
        </button>
      </div>
    </div>
  );
};

export default SGBRibbon;
